import React, { type ComponentProps, type ReactNode } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Flex } from '@atlaskit/primitives/compiled';
import type TeamsAvatar from '@atlaskit/teams-avatar/teams-avatar';
import { token } from '@atlaskit/tokens';

import { TeamInlinePreviewContext } from '../store/context';

import { Avatar } from './components/Avatar';
import { TeamContent } from './components/TeamContent';

type TeamInlinePreviewContextValue = ComponentProps<
	typeof TeamInlinePreviewContext.Provider
>['value'];

type TeamInlinePreviewProps = {
	/** Unique identifier for the team (typically a UUID) */
	teamId: TeamInlinePreviewContextValue['teamId'];
	/** Optional test ID suffix appended to the default test ID for testing purposes */
	testId?: string;
	/** Whether the team is verified or not */
	isVerified?: TeamInlinePreviewContextValue['isVerified'];
	/** Whether the team is archived or not */
	isArchived?: TeamInlinePreviewContextValue['isArchived'];
	/** Whether to display content in a single line layout (defaults to true) */
	singleLine?: TeamInlinePreviewContextValue['singleLine'];
	/** Whether to preserve badges when text truncates (defaults to true) */
	preserveBadges?: TeamInlinePreviewContextValue['preserveBadges'];
	/** Whether to hide the team avatar */
	hideAvatar?: boolean;
	/** Optional custom avatar URL to override the default team avatar */
	overrideAvatarUrl?: ComponentProps<typeof Avatar>['src'];
	/** Size of the avatar defaults to small*/
	avatarSize?: ComponentProps<typeof TeamsAvatar>['size'];
	/** Whether to show a border around the avatar defaults to false */
	showBorder?: boolean;
	/** Whether to render the team type instead of the description */
	showType?: boolean;
	/** Optional team type text to render beneath the team name */
	teamType?: ComponentProps<typeof TeamContent>['teamType'];
	/** The team name text to display */
	teamName: ComponentProps<typeof TeamContent>['teamName'];
	/** Optional additional description text or internationalized message (FormattedMessage) */
	description?: ComponentProps<typeof TeamContent>['description'] | ReactNode;
};

const styles = cssMap({
	containerWithBorder: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		paddingInline: token('space.050'),
		paddingBlock: token('space.025'),
		width: 'fit-content',
	},
});

/**
 * Displays a team's information inline with an avatar, team name, optional description, and status badges.
 * Use this component to show team details in a compact format within lists, cards, or other UI elements.
 */
function TeamInlinePreview(props: TeamInlinePreviewProps): React.JSX.Element {
	return (
		<TeamInlinePreviewProvider
			teamId={props.teamId}
			testId={props.testId}
			isVerified={props.isVerified}
			isArchived={props.isArchived}
			singleLine={props.singleLine}
			preserveBadges={props.preserveBadges}
			showBorder={props.showBorder}
		>
			{props.hideAvatar ? null : <Avatar src={props.overrideAvatarUrl} size={props.avatarSize} />}
			<TeamContent
				teamName={props.teamName}
				description={props.description}
				teamType={props.teamType}
				showType={props.showType}
			/>
		</TeamInlinePreviewProvider>
	);
}

function TeamInlinePreviewProvider({
	teamId,
	testId: testIdPrefix,
	isVerified = false,
	isArchived = false,
	singleLine = true,
	preserveBadges = true,
	showBorder = false,
	children,
}: Pick<
	TeamInlinePreviewProps,
	'testId' | 'isVerified' | 'isArchived' | 'singleLine' | 'preserveBadges' | 'teamId' | 'showBorder'
> & {
	children?: React.ReactNode;
}) {
	const testId = `team-inline-preview-${teamId}`;
	const providerTestId = `${testId}${testIdPrefix ? `--${testIdPrefix}` : ''}`;

	const getTestId = (suffix: string) =>
		testId ? `${testId}-${suffix}${testIdPrefix ? `--${testIdPrefix}` : ''}` : undefined;

	return (
		<Flex
			gap="space.100"
			alignItems="center"
			testId={providerTestId}
			xcss={cx(showBorder && styles.containerWithBorder)}
		>
			<TeamInlinePreviewContext.Provider
				value={{
					teamId,
					isVerified,
					isArchived,
					singleLine,
					preserveBadges,
					getTestId,
				}}
			>
				{children}
			</TeamInlinePreviewContext.Provider>
		</Flex>
	);
}

export { TeamInlinePreview, type TeamInlinePreviewProps };
